import type { Facet } from '@stalvi/contracts';
import { type ReactNode, useEffect, useId, useRef } from 'react';
import { focusableElementsIn, trapTabKey } from '../hooks/useFocusTrap';

interface FiltersSheetProps {
  brands: Facet[];
  selectedBrands: string[];
  onToggleBrand: (id: string) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  // Mobile dialog state. If `mobileOpen` is true, `onMobileClose` is required.
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function FiltersSheet({
  brands,
  selectedBrands,
  onToggleBrand,
  onClearFilters,
  hasActiveFilters,
  mobileOpen,
  onMobileClose,
}: FiltersSheetProps) {
  const bodyProps = {
    brands,
    selectedBrands,
    onToggleBrand,
    onClearFilters,
    hasActiveFilters,
  };
  return (
    <>
      <aside className="card p-4 h-max hidden lg:block">
        <FiltersBody {...bodyProps} />
      </aside>
      {mobileOpen && onMobileClose && (
        <MobileFiltersDialog onClose={onMobileClose}>
          <FiltersBody {...bodyProps} />
        </MobileFiltersDialog>
      )}
    </>
  );
}

function FiltersBody({
  brands,
  selectedBrands,
  onToggleBrand,
  onClearFilters,
  hasActiveFilters,
}: Omit<FiltersSheetProps, 'mobileOpen' | 'onMobileClose'>) {
  const titleId = useId();
  return (
    <div>
      <h2 id={titleId} className="font-semibold mb-3">
        Marca
      </h2>
      {brands.length === 0 && <p className="text-sm text-text-tertiary">Sin marcas</p>}
      <ul className="flex flex-col gap-2">
        {brands.slice(0, 20).map((b) => (
          <li key={b.id}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedBrands.includes(b.id)}
                onChange={() => onToggleBrand(b.id)}
              />
              <span className="flex-1 truncate">{b.label}</span>
              <span className="text-xs text-text-tertiary">({b.count})</span>
            </label>
          </li>
        ))}
      </ul>
      {hasActiveFilters && (
        <button type="button" className="btn btn-ghost btn-sm mt-3" onClick={onClearFilters}>
          Limpiar filtros
        </button>
      )}
    </div>
  );
}

function MobileFiltersDialog({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Remember the element that was focused before the dialog opened; restore on close.
  const returnFocusTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    returnFocusTo.current = document.activeElement as HTMLElement | null;
    // Focus the first focusable element inside the sheet.
    const el = sheetRef.current;
    if (el) focusableElementsIn(el)[0]?.focus();
    return () => {
      returnFocusTo.current?.focus?.();
    };
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      const wrap = trapTabKey(e, sheetRef.current);
      if (wrap) {
        e.preventDefault();
        wrap.focusTarget.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 bg-black/40 flex items-end lg:hidden"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={sheetRef} className="bg-bg-card w-full max-h-[80vh] overflow-auto rounded-t-lg p-4 card">
        <div className="flex items-center justify-between mb-3">
          <h2 id={titleId} className="font-semibold">
            Filtros
          </h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            aria-label="Cerrar filtros"
          >
            Cerrar
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
